import { Download, Upload } from 'lucide-react'
import { useState, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { NativeSelect } from '@/components/ui/native-select'
import { formatDatum } from '@/lib/format'
import { toast } from '@/lib/toast'

/** A file read earlier, as the server reports it. */
export interface PreviousImport {
  id: string
  created_at: string
  created_by_name: string | null
  row_count: number
}

/** The part of a parsed file the picker shows. */
export interface PickedTable {
  sheets: string[]
  sheet: string | null
  header_row: number
  headers: string[]
  rows: string[][]
  previous_imports: PreviousImport[]
}

export interface ReadOptions {
  sheet?: string | null
  headerRow?: number | null
}

interface FilePickerProps<P extends PickedTable> {
  file: File | null
  parsed: P | null
  /** The new file with its table, or null when it could not be read. */
  onParsed: (file: File, parsed: P | null) => void
  /** The module's parse call. */
  parse: (file: File, options: ReadOptions) => Promise<P>
  downloadTemplate: () => Promise<void>
  templateLabel: string
  /** Said after the "already imported" warning. */
  reimportHint?: string
  /** The module's list settings; a function gets whether a file is being read. */
  children?: ReactNode | ((state: { reading: boolean }) => ReactNode)
}

const LABEL = 'flex flex-col gap-1 text-sm font-semibold text-foreground'

/**
 * Bestand (shared by the lead import, spec-60 B2, and the CRM import, spec-63 E6): the
 * file, the sheet and header row, the template, and a warning for a file that was
 * imported before. The module's own settings follow as `children`.
 */
export function FilePicker<P extends PickedTable>({
  file,
  parsed,
  onParsed,
  parse,
  downloadTemplate,
  templateLabel,
  reimportHint = 'Je kunt het opnieuw importeren.',
  children,
}: FilePickerProps<P>) {
  const [reading, setReading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [headerRow, setHeaderRow] = useState<string | null>(null)

  /**
   * Read a file. A new file that cannot be read replaces the one before. Another sheet or
   * header row of the file already read that cannot be read keeps the table read before,
   * with its sheets, so the user picks again instead of choosing the file anew.
   */
  async function read(next: File, options: ReadOptions = {}) {
    const before = next === file ? parsed : null
    setReading(true)
    setError(null)
    try {
      onParsed(next, await parse(next, options))
      setHeaderRow(null)
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : 'Bestand inlezen mislukt.'
      if (before) {
        setHeaderRow(null)
        const otherSheet = options.sheet && options.sheet !== before.sheet
        setError(otherSheet ? `Tabblad ‘${options.sheet}’: ${message}` : message)
      } else {
        onParsed(next, null)
        setError(message)
      }
    } finally {
      setReading(false)
    }
  }

  function choose(next: File | undefined) {
    if (next) void read(next)
  }

  function rereadFromRow() {
    const row = Number(headerRow)
    if (!file || !parsed || headerRow === null || !Number.isInteger(row) || row < 1) return
    if (row !== parsed.header_row) void read(file, { sheet: parsed.sheet, headerRow: row })
  }

  return (
    <>
      <section className="vonk-card flex flex-col gap-3 p-4" aria-labelledby="import-file">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 id="import-file" className="text-base font-semibold text-foreground">
            Bestand
          </h2>
          <Button
            type="button"
            variant="outline"
            onClick={() => void downloadTemplate().catch(() => toast.error('Sjabloon downloaden mislukt.'))}
          >
            <Download /> {templateLabel}
          </Button>
        </div>
        <label
          className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-border bg-background p-6 text-sm text-muted-foreground focus-within:ring-2 focus-within:ring-ring"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            event.preventDefault()
            choose(event.dataTransfer.files[0])
          }}
        >
          <Upload className="size-5" aria-hidden />
          <span>
            {file ? file.name : 'Sleep een .xlsx- of .csv-bestand hierheen, of klik om er een te kiezen'}
          </span>
          <input
            type="file"
            accept=".xlsx,.csv"
            className="sr-only"
            aria-label="Bestand kiezen"
            // A browser fires no `change` for the file that is already chosen: forget it
            // when the picker opens, so a corrected file of the same name is read again.
            onClick={(event) => {
              event.currentTarget.value = ''
            }}
            onChange={(event) => choose(event.target.files?.[0])}
          />
        </label>
        {reading ? <p className="text-sm text-muted-foreground">Bezig met inlezen…</p> : null}
        {error ? (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        ) : null}
        {parsed && file ? (
          <div className="flex flex-wrap items-end gap-4">
            {parsed.sheets.length > 1 ? (
              <label className={LABEL}>
                Tabblad
                <NativeSelect
                  aria-label="Tabblad"
                  value={parsed.sheet ?? ''}
                  onChange={(event) => void read(file, { sheet: event.target.value })}
                >
                  {parsed.sheets.map((sheet) => (
                    <option key={sheet} value={sheet}>
                      {sheet}
                    </option>
                  ))}
                </NativeSelect>
              </label>
            ) : null}
            <label className={LABEL}>
              Kopregel staat op rij
              <Input
                type="number"
                min={1}
                aria-label="Kopregel staat op rij"
                value={headerRow ?? String(parsed.header_row)}
                onChange={(event) => setHeaderRow(event.target.value)}
                onBlur={rereadFromRow}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') event.currentTarget.blur()
                }}
                className="w-24"
              />
            </label>
            <p className="text-sm text-muted-foreground">
              {parsed.rows.length} rijen, {parsed.headers.length} kolommen
            </p>
          </div>
        ) : null}
        {parsed && parsed.previous_imports.length > 0 ? (
          <p role="status" className="rounded-lg bg-warning-subtle p-3 text-sm text-warning">
            Dit bestand is al geïmporteerd:{' '}
            {parsed.previous_imports
              .map(
                (item) =>
                  `${formatDatum(item.created_at)} door ${item.created_by_name ?? 'onbekend'} (${item.row_count} rijen)`,
              )
              .join('; ')}
            . {reimportHint}
          </p>
        ) : null}
      </section>
      {typeof children === 'function' ? children({ reading }) : children}
    </>
  )
}
